/**
 * Product Master Price List — fast prefix / exact search for billing autocomplete.
 */
import { db } from "../firebase";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import {
  normalizeProductMasterDoc,
  PRODUCT_MASTER_COLLECTION,
  PRODUCT_PART_NO_FIELDS,
} from "../config/productMasterFirestore";

export function productItemKey(it) {
  return String(it?.id || it?.partno || it?.code || "").trim().toLowerCase();
}

/** Remove duplicates (lookup cache stores same item under multiple keys). */
export function dedupeProductList(items) {
  const seen = new Set();
  const out = [];
  for (const it of items) {
    const k = productItemKey(it);
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(it);
  }
  return out;
}

function rankProductMatch(it, qlLower) {
  const part = String(it.partno || it.code || "").toLowerCase();
  const alias = String(it.alias || it.desc || "").toLowerCase();
  if (!qlLower) return 0;
  if (part.startsWith(qlLower)) return 0;
  if (part.includes(qlLower)) return 1;
  if (alias.startsWith(qlLower)) return 2;
  if (alias.includes(qlLower)) return 3;
  return 4;
}

/**
 * Filter in-memory lookup cache; best prefix matches first.
 * @param {Map<string, object>} cacheMap
 */
export function filterAndSortCachedProducts(cacheMap, q, max = 80) {
  const ql = String(q ?? "").trim();
  if (!ql) {
    return dedupeProductList(Array.from(cacheMap.values())).slice(0, 25);
  }
  const qlLower = ql.toLowerCase();
  const hay = (it) =>
    `${it.partno || ""} ${it.code || ""} ${it.alias || ""} ${it.desc || ""} ${it.name || ""} ${it["item name"] || ""} ${it.productid || it.productId || ""} ${it.brand || ""} ${it.barcode || ""} ${it.group || ""}`
      .toLowerCase();

  const matched = dedupeProductList(Array.from(cacheMap.values())).filter((it) =>
    hay(it).includes(qlLower)
  );
  matched.sort((a, b) => {
    const ra = rankProductMatch(a, qlLower);
    const rb = rankProductMatch(b, qlLower);
    if (ra !== rb) return ra - rb;
    return String(a.partno || a.code || "").localeCompare(String(b.partno || b.code || ""));
  });
  return matched.slice(0, max);
}

function prefixEndFor(key) {
  return `${key}\uf8ff`;
}

/**
 * Firestore prefix search on part number (tries case variants + field names).
 * @param {string} prefix
 * @param {number} max
 */
export async function queryProductMasterByPartPrefix(prefix, max = 80) {
  const ql = String(prefix ?? "").trim();
  if (!ql) return [];

  const variants = [...new Set([ql, ql.toUpperCase(), ql.toLowerCase()].filter(Boolean))];
  const fields = ["part no", "partno"];
  const merged = [];
  const seen = new Set();

  for (const searchKey of variants) {
    const end = prefixEndFor(searchKey);
    for (const field of fields) {
      try {
        const snap = await getDocs(
          query(
            collection(db, PRODUCT_MASTER_COLLECTION),
            where(field, ">=", searchKey),
            where(field, "<=", end),
            limit(max)
          )
        );
        for (const d of snap.docs) {
          const it = normalizeProductMasterDoc(d);
          const k = productItemKey(it);
          if (!k || seen.has(k)) continue;
          seen.add(k);
          merged.push(it);
          if (merged.length >= max) return merged;
        }
      } catch (err) {
        console.warn(`Product Master prefix (${field}) failed:`, err);
      }
    }
    if (merged.length >= max) break;
  }

  merged.sort((a, b) =>
    String(a.partno || a.code || "").localeCompare(String(b.partno || b.code || ""))
  );
  return merged;
}

/**
 * Exact part-no lookup (case-insensitive across schema fields).
 * @param {string} rawCode
 */
export async function findProductMasterByPartNo(rawCode) {
  const code = String(rawCode ?? "").trim();
  if (!code) return null;

  const tries = [...new Set([code, code.toUpperCase(), code.toLowerCase()])];
  for (const value of tries) {
    for (const field of PRODUCT_PART_NO_FIELDS) {
      try {
        const snap = await getDocs(
          query(
            collection(db, PRODUCT_MASTER_COLLECTION),
            where(field, "==", value),
            limit(1)
          )
        );
        if (!snap.empty) return normalizeProductMasterDoc(snap.docs[0]);
      } catch (err) {
        console.warn(`Product Master exact (${field}) failed:`, err);
      }
    }
  }
  return null;
}

let warmInflight = null;

/**
 * One-time light prefetch so empty focus shows recent parts (does not load full catalog).
 * @param {Map<string, object>} cacheMap — same structure as billing itemCacheRef
 * @param {number} prefetchLimit
 */
export async function warmProductMasterCache(cacheMap, prefetchLimit = 50) {
  if (cacheMap.size >= 15) return 0;
  if (warmInflight) return warmInflight;

  warmInflight = (async () => {
    try {
      const snap = await getDocs(
        query(
          collection(db, PRODUCT_MASTER_COLLECTION),
          orderBy("part no"),
          limit(prefetchLimit)
        )
      );
      let n = 0;
      for (const d of snap.docs) {
        const it = normalizeProductMasterDoc(d);
        const k = String(it.partno || it.code || "").trim().toLowerCase();
        if (!k) continue;
        cacheMap.set(k, it);
        if (it.partno) cacheMap.set(String(it.partno).toLowerCase(), it);
        if (it.code && it.code !== it.partno) cacheMap.set(String(it.code).toLowerCase(), it);
        n += 1;
      }
      return n;
    } catch (err) {
      try {
        const snap = await getDocs(
          query(collection(db, PRODUCT_MASTER_COLLECTION), limit(prefetchLimit))
        );
        for (const d of snap.docs) {
          const it = normalizeProductMasterDoc(d);
          const k = String(it.partno || it.code || "").trim().toLowerCase();
          if (k) cacheMap.set(k, it);
        }
        return snap.size;
      } catch (e2) {
        console.warn("Product Master warm cache failed:", e2);
        return 0;
      }
    } finally {
      warmInflight = null;
    }
  })();

  return warmInflight;
}
